import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getProductCartQty,
  getProductStock,
  incrementCartQty,
  decrementCartQty,
  getProductColours,
} from '../utils/cartHelper';

/**
 * Reusable ProductCartButton component
 * Transitions between "Add to Bag" and "[ − ] {qty} in Bag [ + ]"
 * Synchronizes across all product cards, Navbar, and Cart drawer.
 */
const ProductCartButton = ({
  product,
  colour = null,
  size = null,
  variant = null,
  buttonText = "Add to Bag",
  className = "",
  style = {},
  compact = false,
  onOpenProduct = null,
  onSuccess = null,
}) => {
  const navigate = useNavigate();
  const [cartQty, setCartQty] = useState(0);
  const [loading, setLoading] = useState(false);

  const productId = Number(product?.id || product?.product_id);

  // Sync quantity from unified cart
  const syncQty = useCallback(() => {
    if (!productId) return;
    const qty = getProductCartQty(productId, colour, size);
    setCartQty(qty);
  }, [productId, colour, size]);

  useEffect(() => {
    syncQty();

    const handleCartChange = () => {
      syncQty();
    };

    window.addEventListener('vanya_cart_change', handleCartChange);
    window.addEventListener('storage', handleCartChange);

    return () => {
      window.removeEventListener('vanya_cart_change', handleCartChange);
      window.removeEventListener('storage', handleCartChange);
    };
  }, [syncQty]);

  if (!product) return null;

  const availableStock = getProductStock(product, variant, colour, size);
  const isOutOfStock = availableStock <= 0;

  // Handle Initial Add
  const handleInitialAdd = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isOutOfStock || loading) return;

    // Check multiple colour selection requirement
    const colours = getProductColours(product);
    if (colours.length > 1 && !colour) {
      if (typeof onOpenProduct === 'function') {
        onOpenProduct(productId);
      } else {
        alert("Please select a colour first.");
        navigate(`/product/${productId}`);
      }
      return;
    }

    try {
      setLoading(true);
      const res = await incrementCartQty(product, { colour, size, variant });
      if (res.needsColourSelection) {
        if (typeof onOpenProduct === 'function') {
          onOpenProduct(productId);
        } else {
          alert("Please select a colour first.");
          navigate(`/product/${productId}`);
        }
        return;
      }

      if (res.outOfStock) {
        alert(res.message || "This saree is currently out of stock.");
        return;
      }

      if (res.maxReached) {
        alert(res.message || `Only ${availableStock} item(s) available.`);
        return;
      }

      if (typeof onSuccess === 'function') {
        onSuccess(res.message || `${product.name || 'Product'} added to bag!`);
      }
    } catch (err) {
      console.error("Cart add error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Increment (+)
  const handlePlus = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (loading || isOutOfStock) return;

    if (cartQty >= availableStock) {
      alert(`Only ${availableStock} item(s) available in stock.`);
      return;
    }

    try {
      setLoading(true);
      const res = await incrementCartQty(product, { colour, size, variant });
      if (res.maxReached) {
        alert(res.message || `Only ${availableStock} item(s) available.`);
      }
    } catch (err) {
      console.error("Increment error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Decrement (−)
  const handleMinus = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (loading) return;

    try {
      setLoading(true);
      await decrementCartQty(product, { colour, size, variant });
    } catch (err) {
      console.error("Decrement error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // OUT OF STOCK BUTTON
  // ============================================================
  if (isOutOfStock) {
    return (
      <button
        type="button"
        disabled
        className={`vanya-cart-btn out-of-stock ${className}`}
        style={{
          flex: 1,
          padding: compact ? "7px 4px" : "9px 12px",
          border: "none",
          borderRadius: "8px",
          background: "#cbd5e1",
          color: "#64748b",
          cursor: "not-allowed",
          fontSize: compact ? "10px" : "11px",
          fontWeight: "700",
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          fontFamily: '"Montserrat", sans-serif',
          boxShadow: "none",
          minHeight: compact ? "34px" : "38px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...style,
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        Out of Stock
      </button>
    );
  }

  // ============================================================
  // QUANTITY CONTROLLER (WHEN ADDED TO CART)
  // [ − ]  {qty} in Bag  [ + ]
  // ============================================================
  if (cartQty > 0) {
    return (
      <div
        className={`vanya-cart-stepper ${className}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #4b2954 0%, #2f1035 100%)",
          color: "#fbeea4",
          border: "1px solid #d4af37",
          borderRadius: "8px",
          padding: compact ? "3px 4px" : "4px 6px",
          minHeight: compact ? "34px" : "38px",
          boxShadow: "0 3px 10px rgba(75, 41, 84, 0.3)",
          boxSizing: "border-box",
          userSelect: "none",
          ...style,
        }}
      >
        {/* MINUS BUTTON */}
        <button
          type="button"
          onClick={handleMinus}
          disabled={loading}
          aria-label="Decrease quantity"
          style={{
            background: "rgba(255, 255, 255, 0.15)",
            border: "none",
            color: "#ffffff",
            width: compact ? "26px" : "28px",
            height: compact ? "26px" : "28px",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.15s ease",
            padding: 0,
            lineHeight: 1,
            touchAction: "manipulation",
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          −
        </button>

        {/* QUANTITY DISPLAY */}
        <span
          style={{
            fontFamily: '"Montserrat", sans-serif',
            fontSize: compact ? "10px" : "11px",
            fontWeight: "700",
            color: "#fbeea4",
            letterSpacing: "0.4px",
            textTransform: "uppercase",
            textAlign: "center",
            padding: "0 4px",
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <span>{cartQty}</span>
          <span style={{ fontSize: compact ? "9px" : "10px", opacity: 0.9 }}>
            {compact ? "in Bag" : "in Bag"}
          </span>
        </span>

        {/* PLUS BUTTON */}
        <button
          type="button"
          onClick={handlePlus}
          disabled={loading || cartQty >= availableStock}
          aria-label="Increase quantity"
          title={cartQty >= availableStock ? "Max stock reached" : "Add one more"}
          style={{
            background: cartQty >= availableStock ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.15)",
            border: "none",
            color: cartQty >= availableStock ? "#888" : "#ffffff",
            width: compact ? "26px" : "28px",
            height: compact ? "26px" : "28px",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: cartQty >= availableStock ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.15s ease",
            padding: 0,
            lineHeight: 1,
            touchAction: "manipulation",
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          +
        </button>
      </div>
    );
  }

  // ============================================================
  // INITIAL "ADD TO BAG" BUTTON
  // ============================================================
  return (
    <button
      type="button"
      className={`vanya-cart-btn ${className}`}
      disabled={loading}
      style={{
        flex: 1,
        padding: compact ? "7px 4px" : "9px 12px",
        border: "none",
        borderRadius: "8px",
        background: "linear-gradient(135deg, #e2bc53 0%, #c99e32 100%)",
        color: "#222222",
        cursor: "pointer",
        fontSize: compact ? "10px" : "11px",
        fontWeight: "700",
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        fontFamily: '"Montserrat", sans-serif',
        touchAction: "manipulation",
        boxShadow: "0 3px 10px rgba(212, 175, 55, 0.35)",
        minHeight: compact ? "34px" : "38px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        opacity: loading ? 0.7 : 1,
        ...style,
      }}
      onClick={handleInitialAdd}
    >
      {loading ? "Adding..." : buttonText}
    </button>
  );
};

export default ProductCartButton;
